import { reaction } from "mobx";
import { trace } from "owa-trace";
import RestClient from "sh-rest-client";
import PollHelper from "sh-application/utility/PollHelper";
import { ISmartScheduleGenerationJobStatusEntity, JobStatus } from "sh-models/job/SmartScheduleGenerationJobStatusEntity";
import { updateSmartSchedulePollingStatus, clearSmartSchedulePollingJob } from "sh-application/components/smartSchedule/mutatorActions/pollingActions";
import { smartSchedulePollingState } from "sh-application/components/smartSchedule/store/smartSchedulePollingState";
import { TeamStore } from "sh-team-store";
import { ECSConfigService, ECSConfigKey } from "sh-services";
import MemberUtils from "../../sh-application/utility/MemberUtils";

/**
 * Service that mirrors the initialization pattern of SyncService for Smart Schedule generation polling.
 * It waits until a tag (group) is selected and then polls the smart schedule generation status
 * until a terminal state is reached (success | failed | canceled).
 */
class SmartScheduleGenerationPollingService {
    private _pollAutorunDisposer: () => void;
    private _pollHelper: PollHelper | undefined;
    private _isPolling: boolean = false;
    private _teamId: string | undefined;
    private readonly _terminalStatuses = [JobStatus.Success, JobStatus.Failure, JobStatus.Canceled, JobStatus.PartialSuccess];

    /**
     * Initialize the polling service. Can be called with or without teamId.
     * If teamId isn't supplied yet, we will wait until TeamStore has it.
     * Safe to call multiple times; subsequent calls are ignored.
     */
    public async initialize(teamId?: string): Promise<void> {
        if (teamId) {
            this._teamId = teamId;
        }
        if (this._pollAutorunDisposer) {
            return; // already initialized
        }
        const getTeamId = () => TeamStore()?.team?.id;
        // reaction runs when derived value changes (team id transition)
        let previousTeamId: string | undefined = undefined;
        this._pollAutorunDisposer = reaction<string | undefined>(
            () => getTeamId(),
            (currentTeamId, _reaction) => {
                if (!currentTeamId) {
                    return; // waiting for a valid team id
                }
                if (!previousTeamId) {
                    // First initialization
                    this._teamId = currentTeamId;
                    this.beginPolling();
                    previousTeamId = currentTeamId;
                    return;
                }
                if (previousTeamId !== currentTeamId) {
                    // Team changed
                    this.stopPolling();
                    this._teamId = currentTeamId;
                    this.beginPolling();
                    previousTeamId = currentTeamId;
                    return;
                }
                // Same team but polling not started yet
                if (!this._isPolling) {
                    this._teamId = currentTeamId;
                    this.beginPolling();
                }
            },
            { fireImmediately: true }
        );
    }

    /**
     * Begin polling smart schedule generation status for the team (tag/group no longer required).
     */
    private beginPolling(): void {
        if (!MemberUtils.isAdmin(TeamStore() && TeamStore().me)) {
            return;
        }

        if (!this._teamId) {
            return;
        }
        this._isPolling = true;
        const intervalMs = 60_000; // 1 minute

        const performPoll = async () => {
            if (!ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableSmartScheduling) && MemberUtils.isAdmin(TeamStore() && TeamStore().me)) {
                trace.info("SmartScheduleGenerationPollingService: poll skipped due to feature disabled." );
                return;
            }
            try {
                 RestClient.getSmartScheduleGenerationJobStatus(this._teamId!)
                    .then((status: ISmartScheduleGenerationJobStatusEntity) => {
                        if (!status) {
                            return;
                        }
                        updateSmartSchedulePollingStatus(status);
                    })
                    .catch(e => {
                        trace.warn(`SmartScheduleGenerationPollingService polling error: ${e?.message}`);
                    });
            } catch (e) {
                trace.warn(`SmartScheduleGenerationPollingService polling error: ${e?.message}`);
            }
        };

        // Kick off the first poll immediately
        performPoll();

        // Set up subsequent polls on the regular interval
        this._pollHelper = new PollHelper(intervalMs, intervalMs, performPoll);
    }

    /**
     * Stop polling and reset state.
     */
    public stopPolling(): void {
        if (this._pollHelper) {
            this._pollHelper.stopPolling();
            this._pollHelper = undefined;
        }
        this._isPolling = false;
        // nothing extra to reset now that tagId logic is removed
    }

    /**
     * Dispose of autorun and polling timer.
     */
    public dispose(): void {
        this.stopPolling();
        if (this._pollAutorunDisposer) {
            this._pollAutorunDisposer();
            this._pollAutorunDisposer = undefined;
        }
    }

    /**
     * Performs an immediate poll regardless of current interval timing.
     * Safe to call before initialize; will attempt to resolve team id.
     */
    public async pollNow(teamId?: string): Promise<ISmartScheduleGenerationJobStatusEntity | undefined> {
        // One-off poll: derive effective team id (explicit param takes precedence) without mutating internal _teamId.
        const effectiveTeamId = teamId || this._teamId || TeamStore()?.team?.id;
        if (!effectiveTeamId) {
            return undefined; // nothing to poll
        }
        try {
            const response = await RestClient.getSmartScheduleGenerationJobStatus(effectiveTeamId);
            if (response) {
                updateSmartSchedulePollingStatus(response);
                return response;
            }
        } catch (e) {
            trace.warn(`SmartScheduleGenerationPollingService pollNow error: ${e?.message}`);
            return undefined;
        }
        return undefined;
    }

    /**
     * Cancels the currently active smart schedule generation job for the resolved team.
     * Updates polling snapshot to Canceled if backend call succeeds.
     * @param teamId Optional explicit teamId; otherwise derives from internal or TeamStore state.
     * @returns true if cancellation succeeded, false otherwise.
     */
    public async cancelActiveJob(teamId?: string, tagId?: string, jobId?: string): Promise<boolean> {
        const effectiveTeamId = teamId || this._teamId || TeamStore()?.team?.id;
        // Derive tagId & jobId from polling snapshot if not provided.
        const polling = smartSchedulePollingState();
        const statusEntity = polling.statusEntity;
        const effectiveTagId = tagId || statusEntity?.tagId;
        const effectiveJobId = jobId || polling.jobId || statusEntity?.jobId;

        if (!effectiveTeamId || !effectiveTagId || !effectiveJobId) {
            trace.warn(`cancelActiveJob missing identifiers teamId='${effectiveTeamId}' tagId='${effectiveTagId}' jobId='${effectiveJobId}'`);
            clearSmartSchedulePollingJob();
            return false;
        }
        let succeeded = false;
        try {
            await RestClient.cancelSmartScheduleJob(effectiveTeamId, effectiveTagId, effectiveJobId);
            succeeded = true;
        } catch (e) {
            trace.warn(`SmartScheduleGenerationPollingService cancelActiveJob error: ${e?.message}`);
        } finally {
            clearSmartSchedulePollingJob();
        }
        return succeeded;
    }
}

const service = new SmartScheduleGenerationPollingService();
export default service as SmartScheduleGenerationPollingService;
